import { Fragment, createElement, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Input, Modal, notification } from "antd";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { $journalApi, journalsQueries, TitlePageFillersDropdown } from "@entities/journal";
import { useCoordinator, useLocalUser, useRouter } from "@shared/lib/hooks";
import { UserBubble } from "@shared/ui";
import { NOTIFICATION_MESSAGES } from "@shared/utils";
import { useTitleSectionsStore, type SectionId } from "../../../journal-title-sections";
import { useValidateTitle } from "../../../journal-title-validation";
import { getChatContext, ChatContextData } from "../utils/chat-context";
import { useGetFirstEmptySection } from "./use-get-first-empty-section";


// Маппинг ошибок на кастомные сообщения для чата
const ERROR_MESSAGES_MAP: Record<number, { 
  title: string; 
  description: string; 
  buttonLabel?: string; 
  sectionId?: SectionId;
  openModal?: boolean;
}> = {
  1: {
    title: 'Не хватает обязательных полей в журнале',
    description: 'Номер журнала не может быть пустым',
    buttonLabel: 'Да, заполнить',
    sectionId: 'journals-info',
  },
  2: {
    title: 'Не хватает обязательных полей в журнале',
    description: 'Заполните Характеристику проекта',
    buttonLabel: 'Да, заполнить',
    sectionId: 'journals-info',
  },
  3: {
    title: 'Не хватает обязательных полей в журнале',
    description: 'Заполните Дату начала строительства',
    buttonLabel: 'Да, заполнить',
    sectionId: 'journals-info',
  },
  4: {
    title: 'Не хватает обязательных полей в журнале',
    description: 'Заполните Дату начала журнала',
    buttonLabel: 'Да, заполнить',
    sectionId: 'journals-info',
  },
  5: {
    title: 'В блоке "Сведения о застройщике…" не хватает обязательной информации',
    description: 'Готовы заполнить?',
    buttonLabel: 'Да, заполнить',
    sectionId: 'developer-company',
  },
  6: {
    title: 'Вам нужно заполнить блок Уполномоченный представитель застройщика либо Уполномоченный представитель технического заказчика, лица, ответственного за эксплуатацию здания, сооружения, или регионального оператора',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 2 не сможет добавить запись.',
    buttonLabel: 'Да, заполнить',
    sectionId: 'developer-company',
  },
  7: {
    title: 'Вы уже заполнили информацию об организации Технического заказчика, но нужно добавить уполномоченного представителя.',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 2 не сможет добавить запись.',
    buttonLabel: 'Да, заполнить',
    sectionId: 'tec-customer-company',
  },
  8: {
    title: 'У организации Технического заказчика отсутствует СРО строительства',
    description: 'Обратитесь к администратору',
    buttonLabel: 'Обратитесь к администратору',
    openModal: true,
  },
  24: {
    title: 'Вам нужно заполнить блок Уполномоченный представитель застройщика по стройконтролю',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 4 не сможет добавить запись.',
    buttonLabel: 'Да, заполнить',
    sectionId: 'developer-company',
  },
  34: {
    title: 'У организации Застройщик не указана контактная информация',
    description: 'Укажите email или телефон организации.',
    buttonLabel: 'Обратитесь к администратору',
    openModal: true,
  },
  27: {
    title: 'Вам нужно заполнить блок Уполномоченный представитель технического заказчика по стройконтролю',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 4 не сможет добавить запись.',
    buttonLabel: 'Да, заполнить',
    sectionId: 'tec-customer-company',
  },
  9: {
    title: 'Укажите Лицо, осуществляющее строительство',
    description: 'Это обязательное поле для продолжения работы',
    buttonLabel: 'Да, заполнить',
    sectionId: 'building-contractor-company',
  },
  10: {
    title: 'Нужно добавить Уполномоченного представителя лица, осуществляющего строительство и Уполномоченный представитель лица, осуществляющего строительство, по вопросам строительного контроля.',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 1 или раздел 3 не сможет добавить запись.',
    buttonLabel: 'Готовы добавить?',
    sectionId: 'building-contractor-company',
  },
  25: {
    title: 'Нужно добавить Уполномоченного представителя застройщика, или технического заказчика, лица, ответственного за эксплуатацию здания, сооружения, или регионального оператора по вопросам строительного контроля',
    description: 'Если этого не сделать, пользователь, заполняющий Раздел 4 не сможет добавить запись.',
    buttonLabel: 'Готовы добавить?',
    sectionId: 'building-contractor-company',
  },
  33: {
    title: 'Важно заполнить Лицо, осуществляющее подготовку проектной документации.',
    description: 'Готовы заполнить?',
    buttonLabel: 'Да, заполнить',
    sectionId: 'documentation-contractor-company',
  },
  18: {
    title: 'Вы уже заполнили информацию об организации Авторского надзора, но нужно добавить уполномоченного представителя.',
    description: 'Готовы заполнить?',
    buttonLabel: 'Да, заполнить',
    sectionId: 'designer-supervisions',
  },
  16: {
    title: 'У представителей должны быть номера НОСТРОЙ или НОПРИЗ в национальном реестре специалистов',
    description: 'Обратитесь к администратору',
    buttonLabel: 'Обратитесь к администратору',
    openModal: true,
  },
  21: {
    title: 'Вы уже заполнили информацию об организации Других лиц, осуществляющих строительство, но нужно добавить уполномоченного представителя.',
    description: 'Готовы заполнить?',
    buttonLabel: 'Да, заполнить',
    sectionId: 'other-developers',
  },
};

const SECTION_DESCRIPTIONS: Record<string, string> = {
  'journals-info': 'Заполните основную информацию: номер журнала, характеристики проекта, дата начала строительства и дата начала журнала.',
  'developer-company': 'Укажите застройщика: компания, контактные данные и уполномоченные представители.',
  'tec-customer-company': 'Заполните данные технического заказчика: компания, СРО строительства и представители.',
  'building-contractor-company': 'Укажите лицо, осуществляющее строительство: компания, контактные данные, представители и контроль.',
  'documentation-contractor-company': 'Заполните данные лица, осуществляющего подготовку проектной документации.',
  'designer-supervisions': 'Укажите организации авторского надзора и их представителей.',
  'other-developers': 'Добавьте данные об организациях, осуществляющих строительство.',
  'documentation-permission': 'Заполните информацию о разрешении на выполнение работ.',
  'documentation-examination': 'Укажите данные о проверке проектной документации.',
};

const SECTION_LABELS: Record<string, string> = {
  'journals-info': 'Информация об объекте',
  'developer-company': 'Сведения о застройщике, эксплуатирующей организации или региональном операторе',
  'tec-customer-company': 'Технический заказчик',
  'building-contractor-company': 'Лицо, осуществляющее строительство',
  'documentation-contractor-company': 'Лицо, осуществляющее подготовку проектной документации',
  'designer-supervisions': 'Авторский надзор',
  'other-developers': 'Другие лица, осуществляющие строительство',
  'documentation-permission': 'Разрешение на строительство',
  'documentation-examination': 'Сведения о положительном заключении экспертизы проектной документации',
};

const ERROR_TO_SECTION: Record<number, SectionId> = {
  1: 'journals-info',
  2: 'journals-info',
  3: 'journals-info',
  4: 'journals-info',
  5: 'developer-company',
  6: 'developer-company',
  24: 'developer-company',
  34: 'developer-company',
  7: 'tec-customer-company',
  8: 'tec-customer-company',
  26: 'tec-customer-company',
  27: 'tec-customer-company',
  9: 'building-contractor-company',
  10: 'building-contractor-company',
  25: 'building-contractor-company',
  33: 'documentation-contractor-company',
  30: 'documentation-contractor-company',
  16: 'designer-supervisions',
  18: 'designer-supervisions',
  31: 'designer-supervisions',
  21: 'other-developers',
  32: 'other-developers',
};

const openRequiredCommentModal = (params: {
  title: string;
  okText: string;
  warningDescription: string;
  onSubmit: (comment: string) => Promise<void>;
}) => {
  let value = "";

  Modal.confirm({
    title: params.title,
    okText: params.okText,
    cancelText: "Отмена",
    centered: true,
    width: 520,
    icon: null,
    content: createElement(Input.TextArea, {
      placeholder: "Введите комментарий",
      rows: 4,
      onChange: (e: any) => {
        value = e?.target?.value ?? "";
      },
    }),
    onOk: async () => {
      const comment = value.toString().trim();
      if (!comment) {
        notification.warning({
          message: NOTIFICATION_MESSAGES.TITLE_COMMENT_REQUIRED, // "Требуется комментарий"
          description: params.warningDescription,
          placement: "topRight",
        });
        return Promise.reject(new Error("comment_required"));
      }

      await params.onSubmit(comment);
      return undefined;
    },
  });
};



const VIEWER_NEW_MESSAGES = [
  "Здравствуйте! Я ИИ-агент Билдокс. Здесь вы можете просмотреть Титульный лист, но Заполняющий пока не начал работу над ним.",
  "Добрый день! Сейчас вы на странице нового Титульного листа — его можно посмотреть, но права редактирования только у Заполняющего Титульного листа.",
  "Приветствую! Это новый Титульный лист. Редактирование доступно только для Заполняющего, но вы можете просматривать его готовность.",
  "Здравствуйте, Я ИИ-агент Билдокс и помогаю работать с Титульным листом. Вы можете следить за его заполнением, но редактировать может только Заполняющий.",
  "Добро пожаловать, я ИИ-ассистент. Это режим просмотра — здесь можно ознакомиться с информацией, а заполнять его будет Заполняющий",
];

export const useChatContext = (params?: {
  isApproveOnly?: boolean;
  canEdit?: boolean;
  canApprove?: boolean;
  canIntegration?: boolean;
  isChatPanelOpen?: boolean;
}): ChatContextData => {
  const { titleId } = useCoordinator() as { titleId: string };
  const router = useRouter();
  const isChatPanelOpen = Boolean(params?.isChatPanelOpen);

  const { data, isLoading: isTitleLoading, isFetching: isTitleFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const titleStatusForSideQueries = (
    data?.mainJournal?.titleJournal as { status?: string } | undefined
  )?.status;
  const fetchSignersWhenPanelClosed =
    titleStatusForSideQueries === "AT_SIGNING";

  const { data: responsibleData } = useQuery({
    ...journalsQueries.getResponsibleCommentList(titleId || ""),
    enabled: !!titleId && isChatPanelOpen,
  });
  const { data: signersData } = useQuery({
    ...journalsQueries.getJournalSignersList(titleId || ""),
    enabled:
      !!titleId && (isChatPanelOpen || fetchSignersWhenPanelClosed),
  });
  const localUser = useLocalUser();
  const queryClient = useQueryClient();
  const { handleSubmit, trigger, watch } = useFormContext();
  const journalnumber = watch("journalnumber");
  const projectCharacteristics = watch("projectCharacteristics");
  const constructionStartDate = watch("constructionStartDate");
  const mainJournalStartDate = watch("mainJournalStartDate");

  const invalidateTitleRelated = () => {
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getResponsibleCommentList(titleId).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
    });
  };

  const navigateToTitleTab = (tab: "general" | "notes" | "details") => {
    const path = router.pathname || "";
    const parts = path.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    const known = ["general", "notes", "details"];
    const baseParts = known.includes(last) ? parts.slice(0, -1) : parts;
    router.navigate(`/${[...baseParts, tab].join("/")}`);
  };
  const { mutate: returnToWork } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/return",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentList(titleId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
        });
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentList(titleId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
        });
      },
    },
  );
  const { mutate: revokeApproval } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/cancel",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentList(titleId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
        });
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentList(titleId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getJournalSignersList(titleId).queryKey,
        });
      },
    },
  );

  const { mutate: sendApproval } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/approve",
  );
  const { mutate: saveTitleJournalCommon } = $journalApi.useMutation(
    "put",
    "/api/v1/journals/title/{titleId}/common",
  );

  const { mutate: sendToGsn } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/send",
  );

  const { mutate: rejectGsn } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/reject/send",
  );

  const { mutate: reviewerApprove } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/reviewerApprove",
  );

  const { mutate: rejectUnderApproval } = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/status/reject/approve",
  );

  const handleSave = handleSubmit(
    async (formData) => {
      saveTitleJournalCommon(
        {
          params: { path: { titleId } },
          body: {
            mainJournalNumber: formData.journalnumber || "",
            projectCharacteristics: formData.projectCharacteristics || "",
            constructionStartDate: formData.constructionStartDate
              ? dayjs(formData.constructionStartDate).toISOString()
              : "",
            constructionEndDate: formData.constructionEndDate
              ? dayjs(formData.constructionEndDate).toISOString()
              : "",
            mainJournalStartDate: formData.mainJournalStartDate
              ? dayjs(formData.mainJournalStartDate).toISOString()
              : "",
            mainJournalEndDate: formData.mainJournalEndDate
              ? dayjs(formData.mainJournalEndDate).toISOString()
              : "",
          },
        } as any,
        {
          onSuccess: () => {
            notification.success({
              message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
              description: NOTIFICATION_MESSAGES.TITLE_SAVED_DESC, // "Титульный лист сохранен"
              placement: "topRight",
            });
            invalidateTitleRelated();
          },
          onError: (error: any) => {
            const status = error?.response?.status || error?.status;
            if (status === 409) {
              notification.error({
                message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                description: NOTIFICATION_MESSAGES.TITLE_SAVE_VALIDATION_ERROR_DESC, // "Сохранение невозможно, проверьте заполненность полей: номер журнала, краткие проектные характеристики объекта, начало строительства, начало ведения журнала, либо уникальность указанного номера журнала"
                placement: "topRight",
                duration: 0,
              });
              return;
            }
            notification.error({
              message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
              description: NOTIFICATION_MESSAGES.TITLE_SAVE_VALIDATION_ERROR_DESC, // "Сохранение невозможно, проверьте заполненность полей: номер журнала, краткие проектные характеристики объекта, начало строительства, начало ведения журнала, либо уникальность указанного номера журнала"
              placement: "topRight",
            });
          },
        },
      );
    },
    async () => {
      if (window.parent !== window) {
        // eslint-disable-next-line no-underscore-dangle, react-compiler/react-compiler
        (window as any).__SKIP_SCROLL_RESTORE__ = true;
      }

      await trigger();

      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
        description: NOTIFICATION_MESSAGES.TITLE_SAVE_VALIDATION_ERROR_DESC, // "Сохранение невозможно, проверьте заполненность полей: номер журнала, краткие проектные характеристики объекта, начало строительства, начало ведения журнала, либо уникальность указанного номера журнала"
        placement: "topRight",
      });
    },
  );
  const openSendApprovalConfirm = () => {
    Modal.confirm({
      title: "Отправка на проверку",
      content: "Вы уверены, что хотите отправить титульный лист на проверку?",
      okText: "Отправить",
      cancelText: "Отмена",
      centered: true,
      icon: null,
      onOk: () => {
        sendApproval(
          { params: { path: { titleId } } } as any,
          {
            onSuccess: () => {
              notification.success({
                message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                description: NOTIFICATION_MESSAGES.TITLE_SENT_TO_REVIEW_DESC, // "Титульный лист отправлен на проверку"
                placement: "topRight",
              });
              setTimeout(() => {
                invalidateTitleRelated();
              }, 5000);
            },
            onError: () => {
              notification.error({
                message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                description: NOTIFICATION_MESSAGES.TITLE_SEND_TO_REVIEW_FAILED_DESC_ALT, // "Не удалось отправить титульный лист на проверку"
                placement: "topRight",
              });
            },
          },
        );
      },
    });
  };

  const openSections = useTitleSectionsStore((state) => state.openSections);
  const activeSection = useTitleSectionsStore((state) => state.activeSectionId);
  const firstEmptySection = useGetFirstEmptySection();
  const { errorList } = useValidateTitle();
  
  const currentErrorIndex = useTitleSectionsStore((state) => state.currentErrorIndex);
  const nextError = useTitleSectionsStore((state) => state.nextError);
  const setCurrentErrorIndex = useTitleSectionsStore((state) => state.setCurrentErrorIndex);

  const sectionOrder = [
    'journals-info',                     
    'developer-company',                 
    'building-contractor-company',       
    'documentation-contractor-company',   
    'tec-customer-company',               
    'designer-supervisions',              
    'other-developers',                   
  ];

  const journal = data?.mainJournal?.titleJournal;
  const titleStatus = (journal as any)?.status as string | undefined;
  // Raw capabilities from ability (CASL). Далее они будут "нормализованы" по статусу ТЛ.
  // Важно: ability может быть ещё не обновлён на экране ТЛ (тайминг / разные места применения permissions),
  // поэтому делаем фолбэк на permissions из getTitleJournal.
  const titlePermissions = ((journal as any)?.permissions || []) as Array<{
    action?: string;
    aclName?: string;
  }>;
  const hasTitlePermission = (actions: string[]) =>
    titlePermissions.some(
      (p) =>
        (p?.aclName || "").toString() === "title_journals" &&
        actions.includes((p?.action || "").toString()),
    );

  const derivedCanEdit = hasTitlePermission(["EDIT", "ADMIN", "ALL_USER"]);
  const derivedCanApprove = hasTitlePermission(["APPROVE", "ADMIN"]);
  const derivedCanIntegration = hasTitlePermission(["INTEGRATION", "ADMIN"]);

  const baseCanEdit = Boolean(params?.canEdit) || derivedCanEdit;
  const baseCanApprove = Boolean(params?.canApprove) || derivedCanApprove;
  const baseCanIntegration = Boolean(params?.canIntegration) || derivedCanIntegration;
  const baseIsApproveOnly = Boolean(baseCanApprove && !baseCanEdit);

  const rawCanEdit = baseCanEdit;
  const rawCanApprove = baseCanApprove;
  const rawCanIntegration = baseCanIntegration;
  const rawIsApproveOnly = baseIsApproveOnly;

  /**
   * Роутинг роли в чате зависит не только от permissions, но и от статуса ТЛ.
   * Это нужно, чтобы, например, Проверяющий (APPROVE) в статусе NEW не видел сообщения Заполняющего.
   */
  const EDIT_AS_FILLER_STATUSES = new Set([
    "NEW",
    "UNDER_APPROVAL",
    "REJECTED",
    "CANCELED",
    "COMMENTS_FROM_GSN", 
  ]);
  const APPROVE_AS_REVIEWER_STATUSES = new Set([
    "UNDER_APPROVAL",
    "PREPARATION_XML",
    "DOCUMENT_ERROR",
  ]);
  // Для роли "Отправитель в ГСН" (INTEGRATION-only) по требованиям:
  // - SENT_TO_GSN, INTERACTION_ERROR_WITH_GSN -> сценарий отправителя
  // - SIGNED -> сценарий viewer (если нет EDIT)
  const INTEGRATION_AS_SENDER_STATUSES = new Set([
    "SENT_TO_GSN",
    "INTERACTION_ERROR_WITH_GSN",
  ]);
  // Админ проекта может иметь и EDIT и INTEGRATION — для него в SIGNED нужен сценарий отправителя.
  const INTEGRATION_AS_SENDER_IF_CAN_EDIT_STATUSES = new Set(["SIGNED"]);

  const statusKey = titleStatus || "";
  const canUseIntegrationChat =
    rawCanIntegration &&
    (INTEGRATION_AS_SENDER_STATUSES.has(statusKey) ||
      (rawCanEdit && INTEGRATION_AS_SENDER_IF_CAN_EDIT_STATUSES.has(statusKey)));
  // Важно: показываем "проверяющего" только если это approve-only (без EDIT),
  // иначе (например, Админ проекта с EDIT+APPROVE) по этим статусам остаётся в сценарии Заполняющего.
  const canUseApproveChat =
    rawIsApproveOnly && rawCanApprove && APPROVE_AS_REVIEWER_STATUSES.has(titleStatus || "");
  const canUseEditChat =
    rawCanEdit && EDIT_AS_FILLER_STATUSES.has(titleStatus || "");

  const effectiveCanIntegration = canUseIntegrationChat;
  const effectiveCanApprove = canUseApproveChat;
  const effectiveCanEdit = !effectiveCanIntegration && !effectiveCanApprove && canUseEditChat;
  const isApproveOnly = effectiveCanApprove && rawIsApproveOnly;
  const canEdit = effectiveCanEdit;
  const canApprove = effectiveCanApprove;
  const canIntegration = effectiveCanIntegration;
  const isViewer = !canEdit && !canApprove && !canIntegration;

  const preparationXmlTimerRef = useRef<number | null>(null);
  const lastPreparationXmlTitleIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (titleStatus !== "PREPARATION_XML") {
      if (preparationXmlTimerRef.current != null) {
        window.clearTimeout(preparationXmlTimerRef.current);
        preparationXmlTimerRef.current = null;
      }
      lastPreparationXmlTitleIdRef.current = null;
      return;
    }

    // Ставим таймер один раз на вход в PREPARATION_XML (на конкретный titleId)
    if (lastPreparationXmlTitleIdRef.current === titleId) {
      return;
    }
    lastPreparationXmlTitleIdRef.current = titleId;

    preparationXmlTimerRef.current = window.setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
      });
      preparationXmlTimerRef.current = null;
    }, 5000);

    return () => {
      if (preparationXmlTimerRef.current != null) {
        window.clearTimeout(preparationXmlTimerRef.current);
        preparationXmlTimerRef.current = null;
      }
    };
  }, [queryClient, titleId, titleStatus]);

  const getUnfilledBlocks = (tj: any): Array<{ label: string; sectionId: SectionId; buttonLabel?: string }> => {
    const result: Array<{ label: string; sectionId: SectionId; buttonLabel?: string }> = [];
    if (!tj) return result;

    const optional: Array<{ label: string; sectionId: SectionId }> = [];

    const objectInfoIncomplete =
      !tj?.mainJournalNumber ||
      !tj?.projectCharacteristics ||
      !tj?.constructionStartDate ||
      !tj?.mainJournalStartDate;

    if (objectInfoIncomplete) {
      result.push({
        label: "Информация об объекте",
        sectionId: "journals-info",
        buttonLabel: "Сохранить",
      });
    }


    if (!tj?.developer?.company?.companyId) {
      result.push({ label: "Сведения о Застройщике / эксплуатирующая / регоператор", sectionId: "developer-company" });
    }

    if (!tj?.buildingContractor?.company?.companyId) {
      result.push({ label: "Лицо, осуществляющее строительство", sectionId: "building-contractor-company" });
    }

    if (!tj?.projectDocumentationContractor?.company?.companyId) {
      result.push({
        label: "Лицо, готовящее проектную документацию",
        sectionId: "documentation-contractor-company",
      });
    }

    if (!tj?.technicalCustomer?.company?.companyId) {
      optional.push({ label: "Технический заказчик", sectionId: "tec-customer-company" });
    }
    if (!tj?.designerSupervisions || tj.designerSupervisions.length === 0) {
      optional.push({ label: "Авторский надзор", sectionId: "designer-supervisions" });
    }
    if (!tj?.otherDevelopers || tj.otherDevelopers.length === 0) {
      optional.push({ label: "Другие лица, осуществляющие строительство", sectionId: "other-developers" });
    }

    const permissions = tj?.documentation?.documentationPermission;
    const hasSelectedPermission = Array.isArray(permissions)
      ? permissions.some((p: any) => p?.selectedDocumentPermission)
      : false;
    if (!hasSelectedPermission) {
      optional.push({ label: "Разрешение на строительство", sectionId: "documentation-permission" });
    }

    const examinations = tj?.documentation?.documentationExamination;
    const hasExaminationData = Array.isArray(examinations) && examinations.length > 0;
    const hasSelectedExamination = hasExaminationData
      ? examinations.some((p: any) => p?.selectedDocumentExamination)
      : true;
    if (!hasSelectedExamination) {
      optional.push({
        label: "Сведения о положительном заключении экспертизы проектной документации",
        sectionId: "documentation-examination",
      });
    }

    return [...result, ...optional];
  };

  const unfilledBlocks = useMemo(() => getUnfilledBlocks(journal as any), [journal]);

  const hasUnsavedObjectInfoFields = useMemo(() => {
    const tj = journal as any;
    if (!tj) return false;
    const n = (v: unknown) => String(v ?? "").trim();
    const toComparableDate = (v: unknown) => {
      if (v == null || v === "") return "";
      const d = dayjs(v as any);
      return d.isValid() ? d.toISOString() : "";
    };
    if (n(journalnumber) !== n(tj.mainJournalNumber)) return true;
    if (n(projectCharacteristics) !== n(tj.projectCharacteristics)) return true;
    if (
      toComparableDate(constructionStartDate) !== toComparableDate(tj.constructionStartDate)
    ) {
      return true;
    }
    if (
      toComparableDate(mainJournalStartDate) !== toComparableDate(tj.mainJournalStartDate)
    ) {
      return true;
    }
    return false;
  }, [
    journal,
    journalnumber,
    projectCharacteristics,
    constructionStartDate,
    mainJournalStartDate,
  ]);

  const hasObjectInfoErrorsInList = useMemo(
    () => errorList.some((e) => [1, 2, 3, 4].includes(e.id)),
    [errorList],
  );

  const getFio = (user?: any) => {
    if (!user) return "";
    return `${user.userSurname ?? user.surname ?? ""} ${user.userFirstName ?? user.firstName ?? ""} ${user.userMiddleName ?? user.middleName ?? ""}`
      .replace(/\s+/g, " ")
      .trim();
  };

  const getFioShort = (user?: any) => {
    if (!user) return "";
    const surname = (user.userSurname ?? user.surname ?? "").toString().trim();
    const first = (user.userFirstName ?? user.firstName ?? "").toString().trim();
    const middle = (user.userMiddleName ?? user.middleName ?? "").toString().trim();
    const f = first ? `${first[0]}.` : "";
    const m = middle ? `${middle[0]}.` : "";
    const initials = `${f}${m}`.trim();
    return `${surname} ${initials}`.trim();
  };

  const reviewerReject = useMemo(() => {
    const approver = (responsibleData as any)?.approvers?.find(
      (x: any) => x?.comment != null,
    );
    if (!approver) return null;
    return {
      fio: getFio(approver.userHistory),
      comment: (approver.comment ?? "").toString(),
    };
  }, [responsibleData]);

  const senderReject = useMemo(() => {
    const sender = (responsibleData as any)?.senders?.find(
      (x: any) => x?.comment != null,
    );
    if (!sender) return null;
    return {
      fio: getFio(sender.userHistory),
      comment: (sender.comment ?? "").toString(),
    };
  }, [responsibleData]);

  const fillerCancel = useMemo(() => {
    const inputter = (responsibleData as any)?.inputters?.find(
      (x: any) => x?.comment != null,
    );
    if (!inputter) return null;
    return {
      fio: getFio(inputter.userHistory),
      comment: (inputter.comment ?? "").toString(),
    };
  }, [responsibleData]);

  const revokerInfo = useMemo(() => {
    const candidates: Array<{ roleKey: string; item: any }> = [];
    const pushMatches = (roleKey: string, list?: any[]) => {
      (list || []).forEach((item: any) => {
        if (item?.comment != null) {
          candidates.push({ roleKey, item });
        }
      });
    };

    pushMatches("inputters", (responsibleData as any)?.inputters);
    pushMatches("approvers", (responsibleData as any)?.approvers);
    pushMatches("senders", (responsibleData as any)?.senders);
    pushMatches("signers", (responsibleData as any)?.signers);

    return candidates[0] ?? null;
  }, [responsibleData]);

  const signerWithComment = useMemo(() => {
    const list = (signersData as any)?.signers || [];
    const signer = list.find((s: any) => s?.comment != null) || null;
    if (!signer) return null;
    return {
      fioShort: getFioShort(signer.userInfo),
      representativeRole: (signer.representativeRole ?? "").toString(),
      comment: (signer.comment ?? "").toString().trim(),
    };
  }, [signersData]);

  const anyRejectionComment = useMemo(() => {
    const candidates = [
      revokerInfo?.item?.comment,
      reviewerReject?.comment,
      senderReject?.comment,
      fillerCancel?.comment,
    ]
      .map((c) => (c ?? "").toString().trim())
      .filter(Boolean);

    if (candidates.length > 0) return candidates[0];

    const inputters = (responsibleData as any)?.inputters || [];
    const approvers = (responsibleData as any)?.approvers || [];
    const senders = (responsibleData as any)?.senders || [];
    const fromResponsible = [...inputters, ...approvers, ...senders]
      .map((x: any) => (x?.comment ?? "").toString().trim())
      .filter(Boolean);

    return fromResponsible[0] || "";
  }, [
    fillerCancel?.comment,
    reviewerReject?.comment,
    senderReject?.comment,
    revokerInfo?.item?.comment,
    responsibleData,
  ]);

  const formatMoscowDateTime = (value?: string) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "";
    const date = dt.toLocaleDateString("ru-RU", {
      timeZone: "Europe/Moscow",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const time = dt.toLocaleTimeString("ru-RU", {
      timeZone: "Europe/Moscow",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${date} в ${time}`;
  };

  const getUserId = (user?: any): string | undefined => {
    if (!user) return undefined;
    return (
      user.userId ??
      user.user_id ??
      user.id ??
      user.userID ??
      user.user_id?.toString?.() ??
      undefined
    );
  };

  const withUserTooltip = (label: string, userId?: string) => {
    if (!userId) {
      return createElement("span", null, label);
    }
    return createElement(
      UserBubble,
      { userId, trigger: ["hover"] },
      createElement("span", { className: "user-info-trigger" }, label),
    );
  };

  const awaitingActionsNode = (label: string, userId?: string) =>
    createElement(
      Fragment,
      null,
      "Ожидаются действия от ",
      withUserTooltip(label, userId),
    );

  const currentUserId = localUser?.user_id;
  const isSigner = Boolean(
    currentUserId &&
      (signersData as any)?.signers?.some(
        (s: any) => s?.userInfo?.userId === currentUserId,
      ),
  );
  useEffect(() => {
    if (titleStatus === "AT_SIGNING" && isSigner) {
      const allSections: SectionId[] = [
        'journals-info',
        'developer-company',
        'tec-customer-company',
        'building-contractor-company',
        'designer-supervisions',
        'other-developers',
        'documentation-contractor-company',
        'documentation-permission',
        'documentation-examination',
      ];
      openSections(allSections);
    }
  }, [titleStatus, isSigner, openSections]);

  if (isTitleLoading || isTitleFetching) {
    return {
      messages: [
        {
          id: "ai-chat-loading",
          title:
            titleStatus === "PREPARATION_XML"
              ? "Идёт подготовка XML для подписания"
              : "ИИ-агент печатает",
          typing: true,
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "NEW" && isViewer) {
    const key = `ai-chat-viewer-new:${titleId}`;
    const stored = sessionStorage.getItem(key);
    const idx = stored ? Number(stored) : NaN;
    const safeIdx =
      Number.isFinite(idx) && idx >= 0 && idx < VIEWER_NEW_MESSAGES.length
        ? idx
        : Math.floor(Math.random() * VIEWER_NEW_MESSAGES.length);
    sessionStorage.setItem(key, String(safeIdx));

    return {
      messages: [
        {
          id: "viewer-new",
          title: VIEWER_NEW_MESSAGES[safeIdx],
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "PREPARATION_XML") {
    return {
      messages: [
        {
          id: "title-preparation-xml",
          title: "Идёт подготовка XML для подписания",
          typing: true,
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "DOCUMENT_ERROR") {
    const approverUser = (responsibleData as any)?.approvers?.[0]?.userHistory;
    const approverUserId = getUserId(approverUser);
    const isCurrentUserApprover = Boolean(
      currentUserId && approverUserId && currentUserId === approverUserId,
    );
    const showDocumentErrorAction = isApproveOnly || isCurrentUserApprover;

    if (showDocumentErrorAction) {
      return {
        messages: [
          {
            id: "document-error-1",
            title: "В ходе подготовки XML-документа для подписания возникла ошибка.",
          },
          {
            id: "document-error-2",
            title: "Нужно отправить файл повторно.",
          },
        ],
        actions: [
          {
            id: "retry-prepare-xml",
            label: "Отправить повторно",
            variant: "primary",
            onClick: () => {
              const token = localStorage.getItem("token") || "";
              reviewerApprove(
                {
                  params: {
                    path: { titleId },
                    header: { "X-Auth": token },
                  },
                } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.XML_RETRY_PREPARATION_SUCCESS_DESC, // "Запущена повторная подготовка XML"
                      placement: "topRight",
                    });
                    invalidateTitleRelated();
                  },
                  onError: () => {
                    notification.error({
                      message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                      description: NOTIFICATION_MESSAGES.XML_RETRY_PREPARATION_FAILED_DESC, // "Не удалось повторно отправить на подготовку XML"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    return {
      messages: [
        {
          id: "document-error-view-1",
          title: "В ходе подготовки XML-документа для подписания возникла ошибка.",
        },
        {
          id: "document-error-view-2",
          title: awaitingActionsNode(
            "Проверяющего данные титульного листа",
            approverUserId,
          ),
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "INTERACTION_ERROR_WITH_GSN") {
    if (canIntegration) {
      return {
        messages: [
          { id: "gsn-interaction-error-1", title: "В ходе отправки возникла ошибка взаимодействия." },
          { id: "gsn-interaction-error-2", title: "Повторите отправку." },
        ],
        actions: [
          {
            id: "retry-send-to-gsn",
            label: "Повторить отправку",
            variant: "primary",
            onClick: () => {
              sendToGsn(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_SENT_TO_GSN_REVIEW_DESC, // "Титульный лист отправлен на проверку в ГСН"
                      placement: "topRight",
                    });
                    invalidateTitleRelated();
                  },
                  onError: () => {
                    notification.error({
                      message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                      description: NOTIFICATION_MESSAGES.TITLE_SEND_TO_GSN_FAILED_DESC, // "Не удалось отправить титульный лист в ГСН"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    const senderUser = (responsibleData as any)?.senders?.[0]?.userHistory;
    const senderUserId = getUserId(senderUser);
    return {
      messages: [
        { id: "gsn-interaction-error-view-1", title: "В ходе отправки возникла ошибка взаимодействия." },
        {
          id: "gsn-interaction-error-view-2",
          title: awaitingActionsNode("Отправителя в орган Надзора", senderUserId),
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "SENT_TO_GSN") {
    if (canIntegration) {
      return {
        messages: [
          { id: "sent-to-gsn-sender-1", title: "Вы отправили Титульный лист на проверку в орган Надзора." },
          {
            id: "sent-to-gsn-sender-2",
            title:
              "Титульный лист прошел внутреннюю проверку и подписан. Ожидаем проверки в органе Надзора.",
          },
        ],
        actions: [],
      };
    }

    return {
      messages: [
        { id: "sent-to-gsn-1", title: "Титульный лист направлен на проверку." },
        {
          id: "sent-to-gsn-2",
          title:
            "Необходимые блоки заполнены, Титульный лист прошел внутреннюю проверку и подписан. Ожидаем проверки в органе Надзора.",
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "COMMENTS_FROM_GSN") {
    if (canEdit) {
      return {
        messages: [
          { id: "gsn-comments-1", title: "От органа Надзора пришли замечания" },
          { id: "gsn-comments-2", title: "Перейдите ко списку замечаний и внесите изменения в титульный лист." },
          { id: "gsn-comments-3", title: "Нужно отправить файл повторно." },
        ],
        actions: [
          {
            id: "view-gsn-comments",
            label: "Посмотреть",
            variant: "primary",
            onClick: () => navigateToTitleTab("notes"),
          },
        ],
      };
    }

    return {
      // контакты заполняющего
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      messages: [
        { id: "gsn-comments-view-1", title: "От органа Надзора пришли замечания" },
        {
          id: "gsn-comments-view-2",
          title: awaitingActionsNode(
            "Заполняющего титульного листа",
            getUserId((responsibleData as any)?.inputters?.[0]?.userHistory),
          ),
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "REGISTERED_WITH_GSN") {
    if (canEdit) {
      return {
        messages: [
          { id: "gsn-registered-1", title: "Победа! Титульный лист успешно зарегистрирован в органе Надзора." },
          { id: "gsn-registered-2", title: "Сведения о регистрации получены, и вы можете их посмотреть." },
          { id: "gsn-registered-3", title: "Также вы можете подать заявку на внесение изменений в титульный лист." },
        ],
        actions: [
          {
            id: "view-registration",
            label: "Посмотреть",
            variant: "primary",
            onClick: () => navigateToTitleTab("details"),
          },
          {
            id: "apply-changes",
            label: "Подать заявку",
            variant: "secondary",
            onClick: () => {
              notification.info({
                message: NOTIFICATION_MESSAGES.SECTION_UNAVAILABLE, // "Раздел пока недоступен"
                description: "Подскажите, на какой экран/вкладку вести для «Заявки на изменения» — подключу переход.",
                placement: "topRight",
              });
            },
          },
        ],
      };
    }

    return {
      messages: [
        { id: "gsn-registered-view-1", title: "Победа! Титульный лист успешно зарегистрирован в органе Надзора." },
        { id: "gsn-registered-view-2", title: "Сведения о регистрации получены, и вы можете их посмотреть" },
      ],
      actions: [],
    };
  }

  if (titleStatus === "UNDER_APPROVAL") {
    if (isViewer) {
      const inputterUser = (responsibleData as any)?.inputters?.[0]?.userHistory;
      // approverUser сейчас не используется в тексте (по требованиям не показываем ФИО проверяющего)
      const inputterFio = getFio(inputterUser) || "Пользователь";
      const approverUser = (responsibleData as any)?.approvers?.[0]?.userHistory;
      const approverUserId = getUserId(approverUser);

      return {
        messages: [
          {
            id: "under-approval-view-1",
            title: `Пользователь ${inputterFio} отправил на согласование Титульный лист`,
          },
          {
            id: "under-approval-view-2",
            title: awaitingActionsNode(
              "Проверяющего титульного листа",
              approverUserId,
            ),
          },
        ],
        actions: [],
      };
    }

    const actions: ChatContextData["actions"] = [];

    if (!isApproveOnly) {
      actions.push({
        id: "revoke-approval",
        label: "Отозвать",
        variant: "secondary",
        onClick: () => {
          openRequiredCommentModal({
            title: "Отозвать титульный лист",
            okText: "Отозвать",
            warningDescription:
              "Пожалуйста, укажите причину отзыва титульного листа.",
            onSubmit: (comment) =>
              new Promise<void>((resolve, reject) => {
                revokeApproval(
                  {
                    params: { path: { titleId } },
                    body: { comment },
                  } as any,
                  {
                    onSuccess: () => {
                      notification.success({
                        message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                        description: NOTIFICATION_MESSAGES.TITLE_REVOKED_DESC, // "Титульный лист отозван"
                        placement: "topRight",
                      });
                      invalidateTitleRelated();
                      returnToWork(
                        { params: { path: { titleId } } } as any,
                        {
                          onSuccess: () => {
                            resolve();
                          },
                          onError: () => {
                            notification.warning({
                              message: NOTIFICATION_MESSAGES.TITLE_REVOKED_DESC, // "Титульный лист отозван"
                              description: NOTIFICATION_MESSAGES.TITLE_REVOKED_RETURN_WARNING_DESC, // "Не удалось автоматически вернуть документ в работу. Нажмите «Вернуть в работу»."
                              placement: "topRight",
                            });
                            resolve();
                          },
                        },
                      );
                    },
                    onError: () => {
                      notification.error({
                        message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                        description: NOTIFICATION_MESSAGES.TITLE_REVOKE_FAILED_DESC, // "Не удалось отозвать титульный лист"
                        placement: "topRight",
                      });
                      reject(new Error("revoke_failed"));
                    },
                  },
                );
              }),
          });
        },
      });
    }

    actions.push({
      id: "approve-title",
      label: isApproveOnly ? "Всё правильно, принять" : "Проверено",
      variant: "primary",
      onClick: () => {
        const token = localStorage.getItem("token") || "";
        reviewerApprove(
          {
            params: {
              path: { titleId },
              header: { "X-Auth": token },
            },
          } as any,
          {
            onSuccess: () => {
              notification.success({
                message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                description: NOTIFICATION_MESSAGES.TITLE_REVIEWED_DESC, // "Титульный лист проверен"
                placement: "topRight",
              });

              setTimeout(() => {
                invalidateTitleRelated();
              }, 5000);
            },
            onError: () => {
              notification.error({
                message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                description: NOTIFICATION_MESSAGES.TITLE_REVIEW_FAILED_DESC, // "Титульный лист не проверен"
                placement: "topRight",
              });
            },
          },
        );
      },
    });

    if (canApprove) {
      actions.push({
        id: "reject-title",
        label: "Отклонить",
        variant: "secondary",
        onClick: () => {
          openRequiredCommentModal({
            title: "Отклонить титульный лист",
            okText: "Отклонить",
            warningDescription: "Пожалуйста, укажите причину отклонения.",
            onSubmit: (comment) =>
              new Promise<void>((resolve, reject) => {
                rejectUnderApproval(
                  {
                    params: { path: { titleId } },
                    body: { comment },
                  } as any,
                  {
                    onSuccess: () => {
                      notification.success({
                        message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                        description: NOTIFICATION_MESSAGES.TITLE_REJECTED_DESC, // "Титульный лист отклонен"
                        placement: "topRight",
                      });
                      invalidateTitleRelated();
                      resolve();
                    },
                    onError: () => {
                      notification.error({
                        message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                        description: NOTIFICATION_MESSAGES.TITLE_REJECT_FAILED_DESC, // "Не удалось отклонить титульный лист"
                        placement: "topRight",
                      });
                      reject(new Error("reject_failed"));
                    },
                  } as any,
                );
              }),
          });
        },
      });
    }

    return {
      messages: [
        {
          id: "title-under-approval-1",
          title: isApproveOnly
            ? "Проверьте правильность заполнения Титульного листа."
            : "Вы отправили на проверку заполненный титульный лист.",
        },
        {
          id: "title-under-approval-2",
          ...(isApproveOnly
            ? {
                title:
                  "Примите Титульный лист или отклоните,\nесли есть ошибки.",
              }
            : { title: "Вы можете отозвать проверку, чтобы внести изменения." }),
        },
      ],
      actions,
    };
  }

  if (titleStatus === "CANCELED") {
    const when = formatMoscowDateTime((journal as any)?.updateDate);
    const who = fillerCancel?.fio ? `Пользователь ${fillerCancel.fio}` : "Пользователь";
    const whenPart = when ? ` ${when}` : "";
    const commentText = fillerCancel?.comment ? `“${fillerCancel.comment}”` : "";

    if (isViewer) {
      const inputterUser = (responsibleData as any)?.inputters?.[0]?.userHistory;
      const inputterUserId = getUserId(inputterUser);
      return {
        messages: [
          {
            id: "title-canceled-view-1",
            title: `${who} отменил Титульный лист`,
          },
          {
            id: "title-canceled-view-2",
            title: awaitingActionsNode(
              "Заполняющего титульного листа",
              inputterUserId,
            ),
          },
        ],
        actions: [],
      };
    }

    return {
      messages: [
        {
          id: "title-canceled",
          statusIcon: "warning",
          title: "Проверка отозвана",
          description: `Вам необходимо доработать Титульный лист.\n\n${who} отозвал проверку${whenPart}`,
          commentLabel: commentText ? "Комментарий пользователя:" : "",
          commentText,
        },
      ],
      actions: [
        {
          id: "return-to-work",
          label: "Вернуть в работу",
          onClick: () => {
            returnToWork(
              { params: { path: { titleId } } } as any,
              {
                onSuccess: () => {
                  notification.success({
                    message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                    description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                    placement: "topRight",
                  });
                },
                onError: () => {
                  notification.success({
                    message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                    description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                    placement: "topRight",
                  });
                },
              },
            );
          },
        },
      ],
    };
  }

  if (titleStatus === "AT_SIGNING") {
    return {
      messages: [
        {
          id: "title-at-signing",
          badge: { text: "На подписании", color: "blue" },
          title: isSigner
            ? "Нужная информация о Титульном листе уже заполнена, вам осталось только подписать свои блоки."
            : "Титульный лист находится на подписании у назначенных подписантов. Ожидаем от них действий.",
        },
        ...(isSigner
          ? [
              {
                id: "title-at-signing-2",
                title: "Готовы перейти к подписанию?",
              },
            ]
          : []),
      ],
      actions: isSigner
        ? [
            {
              id: "go-to-signing",
              label: "Да, перейти к подписанию",
              variant: "primary",
              onClick: () => {
                const allSections: SectionId[] = [
                  'journals-info',
                  'developer-company',
                  'tec-customer-company',
                  'building-contractor-company',
                  'designer-supervisions',
                  'other-developers',
                  'documentation-contractor-company',
                  'documentation-permission',
                  'documentation-examination',
                ];
                openSections(allSections);
                
                setTimeout(() => {
                  const firstPending = document.querySelector(".represented-container.pending");
                  if (firstPending && "scrollIntoView" in firstPending) {
                    (firstPending as HTMLElement).scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }, 100);
              },
            },
          ]
        : [],
    };
  }

  if (titleStatus === "SIGNED") {
    if (canIntegration) {
      return {
        messages: [
          {
            id: "title-signed-sender",
            title:
              "Остался последний шаг, можно отправить готовый Титульный лист в орган Надзора, либо отклонить, если необходимы правки.",
          },
        ],
        actions: [
          {
            id: "send-to-gsn",
            label: "Отправить в орган Надзора",
            variant: "primary",
            onClick: () => {
              sendToGsn(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_SENT_TO_GSN_REVIEW_DESC, // "Титульный лист отправлен на проверку в ГСН"
                      placement: "topRight",
                    });
                    invalidateTitleRelated();
                  },
                  onError: () => {
                    notification.error({
                      message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                      description: NOTIFICATION_MESSAGES.TITLE_SEND_TO_GSN_FAILED_DESC, // "Не удалось отправить титульный лист в ГСН"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
          {
            id: "reject-gsn",
            label: "Отклонить",
            variant: "secondary",
            onClick: () => {
              openRequiredCommentModal({
                title: "Отклонить титульный лист",
                okText: "Отклонить",
                warningDescription:
                  "Пожалуйста, укажите причину отклонения титульного листа.",
                onSubmit: (comment) =>
                  new Promise<void>((resolve, reject) => {
                    rejectGsn(
                      {
                        params: { path: { titleId } },
                        body: { newStatus: "REJECTED", comment } as any,
                      } as any,
                      {
                        onSuccess: () => {
                          notification.success({
                            message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                            description: NOTIFICATION_MESSAGES.TITLE_GSN_REJECTED_DESC, // "Титульный лист отклонён для ГСН"
                            placement: "topRight",
                          });
                          invalidateTitleRelated();
                          resolve();
                        },
                        onError: () => {
                          notification.error({
                            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
                            description: NOTIFICATION_MESSAGES.TITLE_REJECT_FAILED_DESC, // "Не удалось отклонить титульный лист"
                            placement: "topRight",
                          });
                          reject(new Error("reject_gsn_failed"));
                        },
                      },
                    );
                  }),
              });
            },
          },
        ],
      };
    }

    return {
      messages: [
        { id: "title-signed-view-1", title: "Титульный лист подписан" },
        {
          id: "title-signed-view-2",
          title: awaitingActionsNode(
            "Отправляющего титульный лист в орган Надзора",
            getUserId((responsibleData as any)?.senders?.[0]?.userHistory),
          ),
        },
      ],
      actions: [],
    };
  }

  if (titleStatus === "REJECTED") {
    const rejectionComment = (
      (revokerInfo?.item?.comment ?? anyRejectionComment) ?? ""
    )
      .toString()
      .trim();
    const rejectionCommentText = rejectionComment ? `«${rejectionComment}»` : "Комментариев нет";
    const sectionIdsFromErrors = [
      ...new Set(
        errorList.map((err) => ERROR_TO_SECTION[err.id]).filter(Boolean),
      ),
    ] as string[];
    const blockNames = sectionIdsFromErrors
      .map((sid) => SECTION_LABELS[sid])
      .filter(Boolean);
    const blockText =
      blockNames.length > 0
        ? `Вам необходимо внести исправление в блоке ${blockNames.length === 1 ? `«${blockNames[0]}»` : `(«${blockNames.join("», «")}»)`}.`
        : "Вам необходимо внести исправление в блоке (блоках) титульного листа.";

    const signerPersonCard = signerWithComment
      ? {
          label: "Подписант",
          name: signerWithComment.fioShort || "Подписант",
          description: signerWithComment.representativeRole || undefined,
        }
      : undefined;

    if (isViewer) {
      const rejectFio =
        signerWithComment?.fioShort ||
        reviewerReject?.fio ||
        senderReject?.fio ||
        "Пользователь";
      const inputterUser = (responsibleData as any)?.inputters?.[0]?.userHistory;
      const inputterUserId = getUserId(inputterUser);
      
      return {
        messages: [
          {
            id: "title-rejected-view-unified",
            title: `Пользователь ${rejectFio} отклонил Титульный лист`,
            description: awaitingActionsNode(
              "Заполняющего титульного листа",
              inputterUserId,
            ),
          },
        ],
        actions: [],
      };
    }

    if (signerWithComment) {
      const signerComment = signerWithComment.comment || rejectionComment || "";
      const when = formatMoscowDateTime((journal as any)?.updateDate);
      const whenPart = when ? ` ${when}` : "";
      const rejectorLine = `Пользователь ${signerWithComment.fioShort || "Подписант"} отклонил подписание${whenPart}.`;
      const instructionLine =
        "Чтобы исправить ошибку верните Титульный лист в работу.";

      const descriptionParts = [blockText, rejectorLine, instructionLine].filter(
        Boolean,
      );
      const description = descriptionParts.join("\n\n");

      return {
        messages: [
          {
            id: "title-rejected-signer",
            title: "Титульный лист отклонен",
            statusIcon: "rejected",
            description,
            commentLabel: "Комментарий подписанта:",
            commentText: signerComment ? `«${signerComment}»` : "Комментариев нет",
            personCard: signerPersonCard,
          },
        ],
        actions: [
          {
            id: "return-to-work",
            label: "Вернуть в работу",
            onClick: () => {
              returnToWork(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                  onError: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    if (canEdit && (reviewerReject || senderReject)) {
      const rejecter = reviewerReject || senderReject;
      const commentText = rejectionComment || reviewerReject?.comment || senderReject?.comment || "";
      const when = formatMoscowDateTime((journal as any)?.updateDate);
      const whenPart = when ? ` ${when}` : "";
      const rejecterRole = reviewerReject ? "проверяющего" : "отправителя";
      
      return {
        messages: [
          {
            id: "title-rejected-by-reviewer-or-sender",
            title: "Проверка отклонена",
            statusIcon: "rejected",
            description: `${blockText}\n\nЧтобы исправить ошибку верните Титульный лист в работу.`,
            commentLabel: `Пользователь ${rejecter?.fio} отклонил проверку${whenPart}`,
            commentText: commentText
              ? `Комментарий ${rejecterRole}:\n\n"${commentText}"`
              : "Комментариев нет",
          },
        ],
        actions: [
          {
            id: "return-to-work",
            label: "Вернуть в работу",
            onClick: () => {
              returnToWork(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                  onError: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    if (reviewerReject) {
      const commentText = rejectionComment || reviewerReject.comment || "";
      return {
        messages: [
          {
            id: "title-rejected-reviewer",
            title: "Титульный лист отклонен",
            statusIcon: "rejected",
            description: `${blockText}\n\nЧтобы исправить ошибку верните Титульный лист в работу.`,
            commentLabel: "Комментарий по правкам",
            commentText: commentText || "Комментариев нет",
          },
        ],
        actions: [
          {
            id: "return-to-work",
            label: "Вернуть в работу",
            onClick: () => {
              returnToWork(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                  onError: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    if (senderReject) {
      const commentText = rejectionComment || senderReject.comment || "";
      return {
        messages: [
          {
            id: "title-rejected-sender",
            title: "Титульный лист отклонен",
            statusIcon: "rejected",
            description: `${blockText}\n\nЧтобы исправить ошибку верните Титульный лист в работу.`,
            commentLabel: "Комментарий по правкам",
            commentText: commentText || "Комментариев нет",
          },
        ],
        actions: [
          {
            id: "return-to-work",
            label: "Вернуть в работу",
            onClick: () => {
              returnToWork(
                { params: { path: { titleId } } } as any,
                {
                  onSuccess: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                  onError: () => {
                    notification.success({
                      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                      description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                      placement: "topRight",
                    });
                  },
                },
              );
            },
          },
        ],
      };
    }

    return {
      messages: [
        {
          id: "title-rejected-fallback",
          title: "Титульный лист отклонен",
          statusIcon: "rejected",
          description: canEdit
            ? `${blockText}\n\nЧтобы исправить ошибку верните Титульный лист в работу.`
            : undefined,
          commentLabel: "Комментарий по правкам",
          commentText: rejectionCommentText,
          personCard: signerPersonCard,
        },
      ],
      actions: [
        {
          id: "return-to-work",
          label: "Вернуть в работу",
          onClick: () => {
            returnToWork(
              { params: { path: { titleId } } } as any,
              {
                onSuccess: () => {
                  notification.success({
                    message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                    description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                    placement: "topRight",
                  });
                },
                onError: () => {
                  notification.success({
                    message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
                    description: NOTIFICATION_MESSAGES.TITLE_RETURNED_TO_WORK_DESC, // "Титульный лист возвращен в работу"
                    placement: "topRight",
                  });
                },
              },
            );
          },
        },
      ],
    };
  }
  
  const hasStartedFilling: boolean = Boolean(
    activeSection || 
    journal?.mainJournalNumber ||
    journal?.developer?.company?.companyId ||
    journal?.technicalCustomer?.company?.companyId ||
    journal?.buildingContractor?.company?.companyId ||
    (journal?.designerSupervisions && journal.designerSupervisions.length > 0) ||
    (journal?.otherDevelopers && journal.otherDevelopers.length > 0),
  );

  if (!hasStartedFilling) {
    return getChatContext(
      false,
      firstEmptySection,
      openSections as any,
    );
  }

  const hasRejectionComment = Boolean(anyRejectionComment && anyRejectionComment.trim());

  if (errorList.length > 0) {
    const sectionsToOpen = [...new Set(
      errorList.map((err) => ERROR_TO_SECTION[err.id]).filter(Boolean),
    )] as SectionId[];

    const validErrorIndex = currentErrorIndex >= errorList.length ? 0 : currentErrorIndex;
    const currentError = errorList[validErrorIndex];
    const customErrorMessage = ERROR_MESSAGES_MAP[currentError.id];
    const hasMultipleErrors = errorList.length > 1;
    const isLastError = validErrorIndex === errorList.length - 1;

    const { sectionId, openModal } = customErrorMessage || {};
    const showObjectInfoChatSave =
      canEdit &&
      titleStatus === "NEW" &&
      (hasObjectInfoErrorsInList || hasUnsavedObjectInfoFields);
    
    let descriptionContent: ReactNode = customErrorMessage?.description || '';
    
    if (openModal) {
      descriptionContent = createElement(
        Fragment,
        null,
        customErrorMessage?.description || '',
        ' ',
        'Обратитесь к ',
        createElement(TitlePageFillersDropdown, {
          text: "администратору проекта",
        }),
      );
    }

    const actions: ChatContextData["actions"] = [];

    if (!openModal) {
      actions.push({
        id: `error-${currentError.id}`,
        label: customErrorMessage?.buttonLabel || 'Понял, исправляю',
        onClick: () => {
          if (sectionId) {
            openSections([sectionId]);
            setTimeout(() => {
              const element = document.getElementById(sectionId);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          } else {
            openSections(sectionsToOpen);
            setTimeout(() => {
              const element = document.getElementById(sectionsToOpen[0]);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }
        },
      });
    }

    if (hasMultipleErrors) {
      actions.push({
        id: 'next-error',
        label: isLastError ? 'На первую' : 'Следующая',
        variant: 'secondary',
        onClick: () => {
          if (isLastError) {
            setCurrentErrorIndex(0);
          } else {
            nextError();
          }
        },
      });
    }

    if (showObjectInfoChatSave) {
      actions.push({
        id: "save-title-from-errors",
        label: "Сохранить",
        variant: "secondary",
        onClick: () => handleSave(),
      });
    }

    const errorClickableItems = [
      {
        id: currentError.id,
        description: typeof currentError.description === 'string'
          ? currentError.description
          : 'Проверьте и заполните обязательные поля',
        sectionId: ERROR_TO_SECTION[currentError.id],
      },
    ];

    return {
      messages: [
        {
          id: 'error-list',
          title: customErrorMessage?.title || 'Требуется заполнить обязательные поля',
          description: descriptionContent,
          commentLabel: '',
          commentText: hasMultipleErrors ? `Подсказка ${validErrorIndex + 1} из ${errorList.length}` : '',
          errors: errorClickableItems as any,
        },
      ],
      actions,
    };
  }

  if (titleStatus === "NEW" && errorList.length === 0) {
    if (hasRejectionComment) {
      const rejecterFio = revokerInfo?.item ? getFio(revokerInfo.item.userHistory) : null;
      const rejecterName = rejecterFio || "пользователем";
      
      return {
        messages: [
          {
            id: "title-filled-but-rejected",
            badge: { text: "Требуются правки", color: "yellow" },
            title:
              `Титульный лист заполнен, но был отклонен или отозван ${rejecterName}.`,
            description: "Вам необходимо доработать Титульный лист.",
            commentLabel: "Причина:",
            commentText: `"${anyRejectionComment}"`,
          },
          ...(unfilledBlocks.length > 0
            ? [
                {
                  id: "title-unfilled-blocks",
                  title: "Обратите внимание у вас остались незаполненные блоки",
                  errors: unfilledBlocks.map((b, idx) => ({
                    id: 10_000 + idx,
                    description: b.label,
                    sectionId: b.sectionId,
                    buttonLabel: b.buttonLabel,
                  })) as any,
                },
              ]
            : []),
        ],
        actions: [
          {
            id: "save-title",
            label: "Сохранить",
            variant: "secondary",
            onClick: () => handleSave(),
          },
          {
            id: "send-approval",
            label: "Отправить на проверку",
            variant: "primary",
            onClick: openSendApprovalConfirm,
          },
        ],
      };
    }

    if (unfilledBlocks.length > 0) {
      return {
        messages: [
          {
            id: "title-unfilled-blocks-list",
            title: "Блоки для заполнения",
            errors: unfilledBlocks.map((b, idx) => ({
              id: 10_000 + idx,
              description: b.label,
              sectionId: b.sectionId,
              buttonLabel: b.buttonLabel,
            })) as any,
          },
        ],
        actions: [
          {
            id: "save-title",
            label: "Сохранить",
            variant: "secondary",
            onClick: () => handleSave(),
          },
          {
            id: "send-approval",
            label: "Отправить на проверку",
            variant: "primary",
            onClick: openSendApprovalConfirm,
          },
        ],
      };
    }

    return {
      messages: [
        {
          id: "title-all-filled",
          badge: { text: "Готово", color: "green" },
          title:
            "Успешно заполнены блоки титульного листа, вы можете отправить на проверку.",
        },
      ],
      actions: [
        {
          id: "save-title",
          label: "Сохранить",
          variant: "secondary",
          onClick: () => handleSave(),
        },
        {
          id: "send-approval",
          label: "Отправить на проверку",
          variant: "primary",
          onClick: openSendApprovalConfirm,
        },
      ],
    };
  }

  const targetSection = activeSection || firstEmptySection;
  const currentIndex = sectionOrder.indexOf(targetSection);
  const nextSectionIndex = currentIndex + 1;
  const nextSection = nextSectionIndex < sectionOrder.length ? sectionOrder[nextSectionIndex] : null;

    return {
      messages: [
        {
          id: 'current-section',
          title: SECTION_DESCRIPTIONS[targetSection] || 'Продолжайте заполнение титульного листа',
        },
      ],
      actions: [
        {
          id: 'continue-fill',
          label: 'Да, продолжим',
          onClick: () => {
            if (nextSection) {
              openSections([nextSection as SectionId]);
              setTimeout(() => {
                const element = document.getElementById(nextSection);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }
          },
        },
      ],
    };
};
